import { fetchUserInfo, type UserInfo } from '@/api/user'
import { getCurrentLanguage } from '@hgj/common-tools'
import { getProductCodeByUrl } from '@/utils/common'

/**
 * @description:用户信息
 * @return {*}
 */
const useUserStore = defineStore('user', () => {
  const { domain } = getProductCodeByUrl()
  const enterPriseKey = useStorage(
    `${domain.toUpperCase()}_ENTERPRISE_KEY__`,
    {},
    window.sessionStorage,
  )

  const info = ref<Partial<UserInfo>>({})
  /** HBL 可选的 mpciPartyId，船东 ID 仅用于 MBL 申报 */
  const hblMpciParties = ref<Dict[]>([])
  /** 默认的mpciPartyId */
  const defaultMpciPartyId = ref<string>('')
  /** 海管家mpciPartyId */
  const hgjMpciPartyId = ref<string>('')
  const isLogin = computed(() => !!info.value?.user?.userId)
  const user = computed(() => info.value?.user)
  const isSuperAdmin = computed(() => info.value?.user?.superAdmin)
  const globalSeaCoins = computed(() => info.value?.globalSeaCoins || [])
  const enabledCurrencies = computed(() => info.value?.enabledCurrencies || [])
  const globalSeaCoinCurrencyByLang = computed(() => {
    const lang = getCurrentLanguage()
    const langMap: { [key: string]: any } = {
      'zh-CN': 'CNY',
      vi: 'VND',
      en: 'USD',
    }
    return langMap[lang]
  })
  // 保存的用户的access-token
  const sessionId: Ref<string | null> = ref(null)

  function setSessionId(id: string) {
    sessionId.value = id
  }

  const getSessionId = computed(() => sessionId.value)

  const getUserInfo = async (): Promise<UserInfo> => {
    const res = await fetchUserInfo()
    setSessionId(res?.user?.accessToken || '')
    info.value = res
    const list =
      res.extData?.mpciParties?.map((item: any) => {
        return {
          ...item,
          label: item.mpciPartyIdNative
            ? `${item.mpciPartyId}-UAE Local ID`
            : item.mpciPartyId,
          value: item.mpciPartyId,
        }
      }) || []
    // 船东 ID 只用于 MBL 申报，HBL 表单不下拉展示
    const nonShipownerParties = list.filter(
      (item) => item.mpciPartyIdShipowner !== true,
    )
    // 无 SCAC 变更权限的账号只保留首个非船东 ID 选项，避免过滤后无可选值
    hblMpciParties.value = res.changeHouseScac
      ? nonShipownerParties
      : nonShipownerParties.slice(0, 1)
    defaultMpciPartyId.value = hblMpciParties.value[0]?.mpciPartyId || ''
    hgjMpciPartyId.value =
      res.extData?.mpciParties?.find((item: any) => item.hgj)?.mpciPartyId || ''
    return res
  }

  /**
   * 用户产品是否可用
   */
  const isProjectEnabled = computed(() => info.value?.openStatus == 'ACCEPTED')

  /**
   * 企业是否可用
   */
  const isCompanyEnabled = computed(() => info.value?.user?.enabled)

  return {
    info,
    /** 是否登录 */
    isLogin,
    enterPriseKey,
    hblMpciParties,
    defaultMpciPartyId,
    hgjMpciPartyId,
    user,
    /** 是否是超级管理员 */
    isSuperAdmin,
    getUserInfo,
    setSessionId,
    getSessionId,
    /** 用户产品是否可用 */
    isProjectEnabled,
    /** 企业是否可用 */
    isCompanyEnabled,
    globalSeaCoins,
    globalSeaCoinCurrencyByLang,
    enabledCurrencies,
  }
})

export default useUserStore
